// =============================================================================
// DataRetention (tenant admin)
//
// Standalone page reachable from the tenant-admin sidebar. Lists all 9
// retention-eligible artifact types with the resolved value (tenant
// override if set, else platform default) and lets the tenant admin
// tighten or relax for THEIR tenant. Reads / writes go through the App
// DB edge function `admin-retention-policies` via the existing
// `x-provision-secret` cross-DB pattern.
//
// Super-admin editing of the platform defaults lives in
// /admin/retention-defaults. Tenant admins see the platform default
// here for context but cannot edit it — the edge function rejects
// tenant_admin attempts on (tenant_id IS NULL) rows.
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import {
  Database, Plane, FileText, Hotel, ShieldCheck, Receipt, AlertOctagon,
  RefreshCw, Save, RotateCcw, UserX, Play, Trash2, ShieldAlert,
  MessageSquare,
} from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';
import {
  listRetentionPolicies,
  upsertRetentionPolicy,
  deleteRetentionPolicy,
  fetchTenantAnonRetention,
  updateTenantAnonRetention,
  fetchTenantGuestSignupCap,
  updateTenantGuestSignupCap,
  fetchTenantFeedbackToggle,
  updateTenantFeedbackToggle,
  fetchTenantAnonCleanupHour,
  updateTenantAnonCleanupHour,
  runAnonCleanup,
  type RetentionPolicyRow,
  type AnonCleanupResult,
} from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { formatScheduleConversions } from '@/lib/timeFormat';

const ARTIFACT_TYPES: Array<{
  key:         string;
  label:       string;
  icon:        any;
  description: string;
}> = [
  { key: 'boarding_pass_image',       label: 'Boarding pass images',     icon: Plane,
    description: 'Photos saved to the boarding-passes bucket. The flight record itself stays.' },
  { key: 'scanned_doc_boarding_pass', label: 'Scanned boarding passes',  icon: Plane,
    description: 'Document scanner uploads tagged as boarding pass.' },
  { key: 'scanned_doc_visa',          label: 'Scanned visas',            icon: ShieldCheck,
    description: 'Visas. Default keeps them forever — referenced for years.' },
  { key: 'scanned_doc_passport',      label: 'Scanned passports',        icon: FileText,
    description: 'Passport pages. Default keeps them forever.' },
  { key: 'scanned_doc_hotel',         label: 'Hotel confirmations',      icon: Hotel,
    description: 'Confirmation emails / printouts. Covers post-trip dispute window.' },
  { key: 'scanned_doc_insurance',     label: 'Travel insurance docs',    icon: Receipt,
    description: 'Insurance certificates. Allows for late claims.' },
  { key: 'scanned_doc_other',         label: 'Other scanned documents',  icon: FileText,
    description: 'Anything the scanner couldn\'t classify into a known type.' },
  { key: 'disruption_event',          label: 'Disruption events',        icon: AlertOctagon,
    description: 'Cancellation / diversion / delay history.' },
  { key: 'trip_flight_record',        label: 'Trip flight records',      icon: Plane,
    description: 'The trip_flights row. Default keeps them forever — small payload, journal value.' },
];

export function DataRetentionPage({ tenantId }: { tenantId: string }) {
  const { user } = useAuth();
  const email = user?.email || 'unknown';

  const [rows,    setRows]    = useState<RetentionPolicyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');
  const [editKeep, setEditKeep] = useState(false);

  // ── Anonymous-user (guest) retention override ───────────────────
  // Lives on branding_configs.anon_retention_days_override. NULL means
  // "inherit the platform default" (platform_settings.anon_retention_days).
  const [anonOverride,        setAnonOverride]        = useState<number | null>(null);
  const [anonPlatformDefault, setAnonPlatformDefault] = useState<number>(30);
  const [anonEditing,         setAnonEditing]         = useState(false);
  const [anonEditVal,         setAnonEditVal]         = useState('');
  const [anonSaving,          setAnonSaving]          = useState(false);

  // ── Guest-signup daily cap (abuse prevention) ──────────────────────
  // Tenants can throttle how many "Try the App" guest accounts can be
  // created in a 24h window for their brand. Defaults to 50; set to 0
  // to disable guest signups entirely.
  const [signupCap,         setSignupCap]         = useState<number>(50);
  const [signupCapEditing,  setSignupCapEditing]  = useState(false);
  const [signupCapEditVal,  setSignupCapEditVal]  = useState('');
  const [signupCapSaving,   setSignupCapSaving]   = useState(false);

  // ── Feedback toggle override ──
  const [feedbackOverride,   setFeedbackOverride]   = useState<boolean | null>(null);
  const [feedbackPlatformDefault, setFeedbackPlatformDefault] = useState<boolean>(true);
  const [feedbackSaving,     setFeedbackSaving]     = useState(false);

  // ── Manual cleanup run (scoped to THIS tenant only) ────────────────
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult,  setCleanupResult]  = useState<AnonCleanupResult | null>(null);
  const [cleanupError,   setCleanupError]   = useState<string | null>(null);
  const [confirmOpen,    setConfirmOpen]    = useState(false);

  // ── Scheduled-run hour (UTC) override ──────────────────────────────
  // Lives on branding_configs.anon_cleanup_hour_utc_override. NULL =
  // inherit the platform default (platform_settings.anon_cleanup_hour_utc).
  // The pg_cron job 'anon-cleanup-hourly' fires every hour and picks up
  // tenants whose effective hour matches the current UTC hour.
  const [hourOverride,       setHourOverride]       = useState<number | null>(null);
  const [hourPlatformDefault, setHourPlatformDefault] = useState<number>(4);
  const [hourSaving,         setHourSaving]         = useState(false);

  const saveCleanupHour = async (next: number | null) => {
    setHourSaving(true);
    try {
      await updateTenantAnonCleanupHour(tenantId, next);
      setHourOverride(next);
    } catch (e) {
      console.error('[retention] save tenant cleanup hour failed', e);
      alert(`Failed to save: ${errorMessage(e)}`);
    } finally {
      setHourSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [policyRows, anon, cap, fb, hour] = await Promise.all([
        listRetentionPolicies(tenantId),
        fetchTenantAnonRetention(tenantId).catch(() => ({ override: null, platformDefault: 30 })),
        fetchTenantGuestSignupCap(tenantId).catch(() => 50),
        fetchTenantFeedbackToggle(tenantId).catch(() => ({ override: null, platformDefault: true })),
        fetchTenantAnonCleanupHour(tenantId).catch(() => ({ override: null, platformDefault: 4 })),
      ]);
      setRows(policyRows);
      setAnonOverride(anon.override);
      setAnonPlatformDefault(anon.platformDefault);
      setSignupCap(cap);
      setFeedbackOverride(fb.override);
      setFeedbackPlatformDefault(fb.platformDefault);
      setHourOverride(hour.override);
      setHourPlatformDefault(hour.platformDefault);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // ConfirmDialog gate for saving a 0-day retention override. When
  // open, the modal calls performSaveAnonOverride(0) on confirm.
  const [confirmZeroOpen, setConfirmZeroOpen] = useState(false);

  const performSaveAnonOverride = async (days: number) => {
    setAnonSaving(true);
    try {
      await updateTenantAnonRetention(tenantId, days);
      setAnonOverride(days);
      setAnonEditing(false);
    } catch (e) {
      console.error('[retention] save anon override failed', e);
      alert(`Failed to save: ${errorMessage(e)}`);
      throw e;  // keep ConfirmDialog open on failure
    } finally {
      setAnonSaving(false);
    }
  };

  const saveAnonOverride = async () => {
    const days = parseInt(anonEditVal.trim(), 10);
    if (!Number.isInteger(days) || days < 0 || days > 3650) {
      alert('Enter a number between 0 and 3650 days. 0 = purge every inactive guest immediately.');
      return;
    }
    if (days === 0) {
      // Hand off to the ConfirmDialog instead of window.confirm so
      // the warning matches the rest of the page's modal styling.
      setConfirmZeroOpen(true);
      return;
    }
    await performSaveAnonOverride(days);
  };

  // Shared core. Dry-run calls this directly; the destructive variant
  // is gated by the ConfirmDialog (see confirmOpen).
  const setFeedbackChoice = async (value: boolean | null) => {
    setFeedbackSaving(true);
    try {
      await updateTenantFeedbackToggle(tenantId, value);
      setFeedbackOverride(value);
    } catch (e) {
      console.error('[retention] feedback toggle failed', e);
      alert(`Failed to save: ${errorMessage(e)}`);
    } finally {
      setFeedbackSaving(false);
    }
  };

  const saveSignupCap = async () => {
    const next = parseInt(signupCapEditVal.trim(), 10);
    if (!Number.isInteger(next) || next < 0 || next > 100_000) {
      alert('Enter a number between 0 and 100000.');
      return;
    }
    setSignupCapSaving(true);
    try {
      await updateTenantGuestSignupCap(tenantId, next);
      setSignupCap(next);
      setSignupCapEditing(false);
    } catch (e) {
      console.error('[retention] save signup cap failed', e);
      alert(`Failed to save: ${errorMessage(e)}`);
    } finally {
      setSignupCapSaving(false);
    }
  };

  const runCleanup = async (dryRun: boolean) => {
    setCleanupRunning(true);
    setCleanupError(null);
    setCleanupResult(null);
    try {
      const r = await runAnonCleanup({ tenantId, dryRun });
      setCleanupResult(r);
    } catch (e) {
      console.error('[retention] cleanup failed', e);
      setCleanupError(errorMessage(e));
      throw e;  // bubble so the ConfirmDialog stays open on failure
    } finally {
      setCleanupRunning(false);
    }
  };

  const resetAnonOverride = async () => {
    setAnonSaving(true);
    try {
      await updateTenantAnonRetention(tenantId, null);
      setAnonOverride(null);
    } catch (e) {
      console.error('[retention] reset anon override failed', e);
      alert(`Failed to reset: ${errorMessage(e)}`);
    } finally {
      setAnonSaving(false);
    }
  };

  const formatDays = (d: number | null | undefined) => {
    if (d == null) return 'Keep forever';
    if (d % 365 === 0) return `${d / 365} year${d / 365 === 1 ? '' : 's'}`;
    if (d % 30  === 0) return `${d / 30}  month${d / 30  === 1 ? '' : 's'}`;
    return `${d} day${d === 1 ? '' : 's'}`;
  };

  const beginEdit = (key: string, current: number | null | undefined, hasOverride: boolean) => {
    setEditing(key);
    setEditVal(current != null ? String(current) : '');
    setEditKeep(current === null && hasOverride);
  };

  const save = async (key: string) => {
    setSaving(key);
    try {
      const days = editKeep
        ? null
        : editVal.trim() === '' ? null
        : Math.max(1, parseInt(editVal.trim(), 10) || 0);
      await upsertRetentionPolicy({
        tenant_id:      tenantId,
        artifact_type:  key,
        retention_days: days,
        set_by_role:    'tenant_admin',
        set_by_email:   email,
      });
      setEditing(null);
      await load();
    } catch (e) {
      console.error('[retention] save failed', e);
      alert(`Failed to save: ${errorMessage(e)}`);
    } finally {
      setSaving(null);
    }
  };

  const reset = async (key: string) => {
    setSaving(key);
    try {
      await deleteRetentionPolicy({
        tenant_id:    tenantId,
        artifact_type: key,
        set_by_role:  'tenant_admin',
        set_by_email: email,
      });
      await load();
    } catch (e) {
      console.error('[retention] reset failed', e);
      alert(`Failed to reset: ${errorMessage(e)}`);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Database size={22} className="text-brand-600" />
        <div>
          <h1 className="text-xl font-bold text-surface-900">Data Retention</h1>
          <p className="text-sm text-surface-500">
            Override the platform-default retention windows for this tenant.
          </p>
        </div>
      </div>

      <div className="glass-panel p-4 text-sm text-surface-600">
        A daily cron deletes user-generated artifacts older than the windows below. Override any value to tighten or relax for your tenant. Minimum 1 day. Empty / unset means the platform default applies. Per-row "Keep forever" pins (set by users on individual flights or scanned docs) always win regardless of the policy.
      </div>

      {/* ── Feedback toggle override ────────────────────────────────
          Not strictly a retention setting, but lives here because it
          follows the same platform-default + tenant-override pattern.
          Three states: inherit / force-on / force-off. */}
      {!loading && (
        <div className="glass-panel p-4 border-l-2 border-l-brand-600">
          <div className="flex items-start gap-3">
            <MessageSquare size={18} className="text-brand-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <h3 className="text-sm font-semibold text-surface-900">
                  User feedback collection
                </h3>
                {feedbackOverride !== null && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-100 text-brand-700">
                    Tenant override
                  </span>
                )}
              </div>
              <p className="text-xs text-surface-500 mb-2">
                When enabled, end users see a floating "Feedback" button on this tenant's home page.
                Submissions show up in the Feedback tab. Platform default is{' '}
                <strong>{feedbackPlatformDefault ? 'on' : 'off'}</strong>; you can override below.
              </p>

              <div className="flex items-center gap-3 flex-wrap text-sm">
                <span className="font-bold text-surface-900">
                  Effective: {(feedbackOverride ?? feedbackPlatformDefault) ? 'Enabled' : 'Disabled'}
                </span>
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    onClick={() => setFeedbackChoice(null)}
                    disabled={feedbackSaving}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      feedbackOverride === null
                        ? 'bg-brand-600 text-white'
                        : 'bg-surface-100 text-surface-700 hover:bg-surface-200'
                    } disabled:opacity-50`}
                  >
                    Inherit
                  </button>
                  <button
                    onClick={() => setFeedbackChoice(true)}
                    disabled={feedbackSaving}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      feedbackOverride === true
                        ? 'bg-emerald-600 text-white'
                        : 'bg-surface-100 text-surface-700 hover:bg-surface-200'
                    } disabled:opacity-50`}
                  >
                    On
                  </button>
                  <button
                    onClick={() => setFeedbackChoice(false)}
                    disabled={feedbackSaving}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      feedbackOverride === false
                        ? 'bg-red-600 text-white'
                        : 'bg-surface-100 text-surface-700 hover:bg-surface-200'
                    } disabled:opacity-50`}
                  >
                    Off
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* ════════════════════════════════════════════════════════════
              SECTION: Anonymous (Guest) Accounts
              Retention window + signup cap + manual cleanup all share
              the same subject and the same operator concern, so they
              live in one labeled section instead of being scattered
              between the per-artifact policies and the page footer.
              ════════════════════════════════════════════════════════════ */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <UserX size={16} className="text-brand-600" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-500">
                Anonymous (Guest) Accounts
              </h2>
            </div>

          {/* ── Anonymous (guest) user retention ────────────────────────
              Separate from the artifact policies below — controls how
              long guest accounts (Supabase anonymous sign-ins) survive
              before the cleanup cron removes them and all their data.
              Tenant override on branding_configs; NULL = platform
              default. */}
          <div className="glass-panel p-4 border-l-2 border-l-brand-600">
            <div className="flex items-start gap-3">
              <UserX size={18} className="text-brand-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <h3 className="text-sm font-semibold text-surface-900">
                    Anonymous (guest) accounts
                  </h3>
                  {anonOverride !== null && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-100 text-brand-700">
                      Tenant override
                    </span>
                  )}
                </div>
                <p className="text-xs text-surface-500 mb-2">
                  Visitors who tap "Try the App" get a guest account on your tenant. Inactive guests are cleaned up after this many days.
                </p>

                {!anonEditing && (
                  <div className="text-sm flex items-center gap-3 flex-wrap">
                    {(() => {
                      const effective = anonOverride !== null ? anonOverride : anonPlatformDefault;
                      const label = effective === 0
                        ? 'Purge any inactive guest (0 days)'
                        : `${effective} day${effective === 1 ? '' : 's'} of inactivity`;
                      return (
                        <span className={`font-bold ${effective === 0 ? 'text-red-600' : 'text-surface-900'}`}>
                          {label}
                        </span>
                      );
                    })()}
                    {anonOverride !== null && (
                      <span className="text-xs text-surface-500">
                        Platform default: {anonPlatformDefault === 0 ? 'purge now' : `${anonPlatformDefault} day${anonPlatformDefault === 1 ? '' : 's'}`}
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setAnonEditing(true);
                        setAnonEditVal(String(anonOverride ?? anonPlatformDefault));
                      }}
                      className="ml-auto text-xs text-brand-600 hover:underline"
                    >
                      {anonOverride !== null ? 'Edit override' : 'Set tenant override'}
                    </button>
                    {anonOverride !== null && (
                      <button
                        onClick={resetAnonOverride}
                        disabled={anonSaving}
                        className="text-xs text-surface-500 hover:text-surface-900 inline-flex items-center gap-1"
                        title="Reset to platform default"
                      >
                        <RotateCcw size={12} />
                        Reset
                      </button>
                    )}
                  </div>
                )}
                {/* Warning strip when retention=0 (the destructive value) */}
                {!anonEditing && (anonOverride === 0 || (anonOverride === null && anonPlatformDefault === 0)) && (
                  <div className="mt-1 text-[11px] text-red-600">
                    With 0-day retention, every guest on this tenant who isn't actively signed in becomes eligible the next time cleanup runs.
                  </div>
                )}

                {anonEditing && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="text-xs text-surface-500">Days of inactivity:</label>
                      <input
                        type="number"
                        min={0}
                        max={3650}
                        value={anonEditVal}
                        onChange={(e) => setAnonEditVal(e.target.value)}
                        placeholder={`Default ${anonPlatformDefault}`}
                        className="w-24 px-2 py-1 text-sm bg-surface-0 text-surface-900 border border-surface-300 rounded-md"
                      />
                      <span className="text-[11px] text-surface-500">(0 = purge any inactive guest)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={saveAnonOverride}
                        disabled={anonSaving}
                        className="btn-primary text-xs"
                      >
                        {anonSaving ? <Spinner size="sm" /> : <Save size={12} />}
                        Save
                      </button>
                      <button
                        onClick={() => setAnonEditing(false)}
                        className="text-xs text-surface-500 hover:text-surface-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Guest signup daily cap (abuse prevention) ──────────────
              Caps how many guest accounts can be created on this tenant
              in any 24h window. Combined with the platform-wide per-IP
              cap (3 / IP / 24h, hardcoded in register-anon-signup),
              this stops a single bad actor from draining AI spend by
              re-signing-up over and over. Set 0 to disable guest
              signups outright. */}
          <div className="glass-panel p-4 border-l-2 border-l-amber-500">
            <div className="flex items-start gap-3">
              <ShieldAlert size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <h3 className="text-sm font-semibold text-surface-900">
                    Guest signup cap (per 24h)
                  </h3>
                  {signupCap === 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">
                      Guests disabled
                    </span>
                  )}
                </div>
                <p className="text-xs text-surface-500 mb-2">
                  Maximum number of guest accounts that can be created on this tenant per 24-hour window.
                  Protects your AI spend from repeated abuse. Set to 0 to disable the "Try the App" CTA entirely.
                </p>

                {!signupCapEditing && (
                  <div className="text-sm flex items-center gap-3 flex-wrap">
                    <span className="font-bold text-surface-900">
                      {signupCap === 0 ? 'Disabled (0)' : `${signupCap} signup${signupCap === 1 ? '' : 's'} / day`}
                    </span>
                    <span className="text-xs text-surface-500">
                      Plus a hardcoded 3-signups-per-IP-per-24h cap across the whole platform.
                    </span>
                    <button
                      onClick={() => {
                        setSignupCapEditing(true);
                        setSignupCapEditVal(String(signupCap));
                      }}
                      className="ml-auto text-xs text-brand-600 hover:underline"
                    >
                      Edit
                    </button>
                  </div>
                )}

                {signupCapEditing && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="text-xs text-surface-500">Max guest signups / 24h:</label>
                      <input
                        type="number"
                        min={0}
                        max={100000}
                        value={signupCapEditVal}
                        onChange={(e) => setSignupCapEditVal(e.target.value)}
                        placeholder="50"
                        className="w-24 px-2 py-1 text-sm bg-surface-0 text-surface-900 border border-surface-300 rounded-md"
                      />
                      <span className="text-[11px] text-surface-500">(0 disables guest signups)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={saveSignupCap}
                        disabled={signupCapSaving}
                        className="btn-primary text-xs"
                      >
                        {signupCapSaving ? <Spinner size="sm" /> : <Save size={12} />}
                        Save
                      </button>
                      <button
                        onClick={() => setSignupCapEditing(false)}
                        className="text-xs text-surface-500 hover:text-surface-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Manual cleanup run (scoped to this tenant) ──────────────
              Same logical concern as the retention + signup cap cards
              above — what happens to guest accounts on this tenant.
              Moved up from the bottom of the page so all three
              guest-account controls live together. */}
          <div className="glass-panel p-5">
            <div className="flex items-start gap-3 mb-3">
              <UserX size={20} className="text-brand-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-base font-semibold text-surface-900">Run anonymous-user cleanup</h3>
                <p className="text-xs text-surface-500 mt-0.5">
                  Finds guest accounts on this tenant that have been inactive longer than the effective retention window
                  ({anonOverride !== null
                    ? `tenant override: ${anonOverride}d`
                    : `platform default: ${anonPlatformDefault}d`})
                  and deletes them.
                </p>
              </div>
            </div>

            {/* Schedule control — tenant override for the UTC hour the
                hourly cron picks up this tenant. NULL = inherit platform
                default. Resolved hour shown in the helper text so the
                effective schedule is always visible. Manual Dry-run /
                Run-cleanup-now buttons ignore the schedule. */}
            <div className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded-md bg-surface-50 border border-surface-200">
              <span className="text-xs font-medium text-surface-700">Scheduled run time</span>
              <label className="inline-flex items-center gap-2 text-xs">
                <select
                  value={hourOverride ?? ''}
                  onChange={e => {
                    const raw = e.target.value;
                    saveCleanupHour(raw === '' ? null : parseInt(raw, 10));
                  }}
                  disabled={hourSaving}
                  // bg-surface-0 + text-surface-900 instead of plain
                  // bg-white — without the explicit text color the
                  // select inherited the parent's white text and
                  // rendered invisible. Matches the rest of the
                  // tenant-admin form-input pattern.
                  className="px-2 py-1 rounded-md border border-surface-300 text-xs font-mono bg-surface-0 text-surface-900 focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:opacity-50"
                >
                  <option value="">Inherit ({String(hourPlatformDefault).padStart(2, '0')}:00 UTC)</option>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                  ))}
                </select>
                <span className="text-surface-500">UTC daily</span>
                {hourSaving && <Spinner size="sm" />}
              </label>
              <span className="text-[11px] text-surface-500">
                {hourOverride === null
                  ? `Inheriting platform default (${String(hourPlatformDefault).padStart(2, '0')}:00 UTC).`
                  : `Override active — runs daily at ${String(hourOverride).padStart(2, '0')}:00 UTC.`}
              </span>
              {/* Conversion hint — translates the effective UTC hour
                  (override OR inherited platform default) into US
                  Eastern (EDT + EST) and India IST so the tenant
                  admin doesn't have to do mental math. */}
              <span className="block w-full text-[11px] text-surface-500 mt-1">
                {formatScheduleConversions(hourOverride ?? hourPlatformDefault)}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => runCleanup(true)}
                disabled={cleanupRunning}
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-surface-100 hover:bg-surface-200 text-surface-900 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {cleanupRunning ? <Spinner size="sm" /> : <Play size={12} />}
                Dry run (preview only)
              </button>
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={cleanupRunning}
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {cleanupRunning ? <Spinner size="sm" /> : <Trash2 size={12} />}
                Run cleanup now
              </button>
            </div>

            {cleanupError && (
              <div className="mt-3 p-3 rounded-md bg-red-50 border border-red-200 text-xs text-red-700">
                {cleanupError}
              </div>
            )}

            {cleanupResult && (
              <div className="mt-3 p-3 rounded-md bg-surface-50 border border-surface-200 text-xs space-y-2">
                <div className="font-semibold text-surface-900">
                  {cleanupResult.dryRun ? 'Dry run results' : 'Cleanup complete'}
                </div>
                {(() => {
                  const t = cleanupResult.perTenant[0];
                  if (!t) {
                    return <div className="text-surface-500">No tenant scanned — check that this tenant is active.</div>;
                  }
                  if (t.skipped) {
                    return <div className="text-amber-600">Skipped — {t.skipped}</div>;
                  }
                  return (
                    <>
                      <div className="text-surface-600">
                        Retention window: <strong>{t.anon_retention_days} day{t.anon_retention_days === 1 ? '' : 's'}</strong>
                        {' · '}
                        {cleanupResult.dryRun ? 'Would delete' : 'Deleted'}:{' '}
                        <strong>{cleanupResult.dryRun ? t.candidates.length : t.deleted.length}</strong> account(s)
                      </div>
                      {t.errors.length > 0 && (
                        <div className="space-y-1 pt-1 border-t border-surface-200">
                          <div className="text-red-700 font-medium">
                            {t.errors.length} error{t.errors.length === 1 ? '' : 's'}:
                          </div>
                          {t.errors.slice(0, 10).map((e, i) => (
                            <div key={i} className="text-red-600 font-mono break-all">
                              {e.user_id !== '-' && <span className="text-red-400">{e.user_id.slice(0, 8)}…: </span>}
                              {e.error}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          {/* end SECTION: Anonymous (Guest) Accounts */}
          </div>

          {/* ════════════════════════════════════════════════════════════
              SECTION: Per-Artifact Retention
              Trip-related artifact policies (boarding passes, scanned
              docs, etc.). Same retention pattern but a different
              subject — separated visually so the page reads as two
              clear groups.
              ════════════════════════════════════════════════════════════ */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <Database size={16} className="text-brand-600" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-500">
                Per-Artifact Retention
              </h2>
            </div>

          {ARTIFACT_TYPES.map(({ key, label, icon: Icon, description }) => {
            const platform     = rows.find(r => r.tenant_id === null     && r.artifact_type === key);
            const tenantRow    = rows.find(r => r.tenant_id === tenantId && r.artifact_type === key);
            const hasOverride  = !!tenantRow;
            const effective    = hasOverride ? tenantRow!.retention_days : (platform?.retention_days ?? null);
            const isEditing    = editing === key;
            const isSaving     = saving  === key;

            return (
              <div key={key} className="glass-panel p-4">
                <div className="flex items-start gap-3">
                  <Icon size={18} className="text-brand-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <h3 className="text-sm font-semibold text-surface-900">{label}</h3>
                      {hasOverride && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-100 text-brand-700">
                          Tenant override
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-surface-500 mb-2">{description}</p>

                    {!isEditing && (
                      <div className="text-sm flex items-center gap-3 flex-wrap">
                        <span className={`font-bold ${effective === null ? 'text-emerald-600' : 'text-surface-900'}`}>
                          {formatDays(effective)}
                        </span>
                        {hasOverride && (
                          <span className="text-xs text-surface-500">
                            Platform default: {formatDays(platform?.retention_days)}
                          </span>
                        )}
                        <button
                          onClick={() => beginEdit(key, effective, hasOverride)}
                          className="ml-auto text-xs text-brand-600 hover:underline"
                        >
                          {hasOverride ? 'Edit override' : 'Set tenant override'}
                        </button>
                        {hasOverride && (
                          <button
                            onClick={() => reset(key)}
                            disabled={isSaving}
                            className="text-xs text-surface-500 hover:text-surface-900 inline-flex items-center gap-1"
                            title="Reset to platform default"
                          >
                            <RotateCcw size={12} />
                            Reset
                          </button>
                        )}
                      </div>
                    )}

                    {isEditing && (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <label className="text-xs text-surface-500">Days:</label>
                          <input
                            type="number"
                            min={1}
                            value={editKeep ? '' : editVal}
                            disabled={editKeep}
                            onChange={(e) => setEditVal(e.target.value)}
                            placeholder="e.g. 90"
                            className="w-24 px-2 py-1 text-sm bg-surface-0 text-surface-900 border border-surface-300 rounded-md disabled:opacity-50"
                          />
                          <label className="inline-flex items-center gap-1 text-xs text-surface-700">
                            <input
                              type="checkbox"
                              checked={editKeep}
                              onChange={(e) => setEditKeep(e.target.checked)}
                            />
                            Keep forever
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => save(key)}
                            disabled={isSaving}
                            className="btn-primary text-xs"
                          >
                            {isSaving ? <Spinner size="sm" /> : <Save size={12} />}
                            Save
                          </button>
                          <button
                            onClick={() => setEditing(null)}
                            className="text-xs text-surface-500 hover:text-surface-900"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
          {/* end SECTION: Per-Artifact Retention */}
        </div>
      )}

      <div className="glass-panel p-3 flex items-start gap-2 text-xs text-surface-600">
        <RefreshCw size={12} className="mt-0.5 flex-shrink-0" />
        <span>
          The cleanup cron runs daily at 03:30 UTC. Override changes take effect on the next run. Per-row "Keep forever" pins always win.
        </span>
      </div>

      {/* Destructive-action confirm modal for this tenant's cleanup —
          same component the super admin uses, scoped wording. */}
      {(() => {
        const effective = anonOverride !== null ? anonOverride : anonPlatformDefault;
        const isPurgeAll = effective === 0;
        return (
          <ConfirmDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={() => runCleanup(false)}
            title={isPurgeAll ? 'Purge ALL inactive guests?' : 'Delete inactive guest accounts?'}
            description={
              <>
                {isPurgeAll && (
                  <>
                    The effective retention is currently <strong>0 days</strong>. That means <strong>every guest on this tenant who isn't actively signed in right now</strong> will be deleted, including ones who signed up minutes ago.
                    <br />
                    <br />
                  </>
                )}
                This will <strong>permanently delete</strong> anonymous guest accounts on this tenant
                that have been inactive longer than the effective retention window
                {' '}({anonOverride !== null
                  ? `tenant override of ${anonOverride === 0 ? '0 days (purge now)' : `${anonOverride} day${anonOverride === 1 ? '' : 's'}`}`
                  : `platform default of ${anonPlatformDefault === 0 ? '0 days (purge now)' : `${anonPlatformDefault} day${anonPlatformDefault === 1 ? '' : 's'}`}`}).
                Their profiles, memberships, and saved trips will be removed via cascade.
                <br />
                <br />
                This action cannot be undone. Consider running a Dry run first to preview the
                number of accounts that will be deleted.
              </>
            }
            confirmLabel={isPurgeAll ? 'Yes, purge now' : 'Yes, run cleanup'}
            cancelLabel="Cancel"
            tone="danger"
          />
        );
      })()}

      {/* Save-time confirm when the operator sets the override to 0.
          Setting the value doesn't itself delete anyone — but the next
          cleanup run (cron or manual) will eat every inactive guest.
          We make the consequence explicit before the row is saved. */}
      <ConfirmDialog
        open={confirmZeroOpen}
        onClose={() => setConfirmZeroOpen(false)}
        onConfirm={() => performSaveAnonOverride(0)}
        title="Save 0-day retention?"
        description={
          <>
            Saving <strong>0</strong> means every guest on this tenant who isn't actively signed
            in right now becomes <strong>eligible for deletion the next time cleanup runs</strong>.
            <br />
            <br />
            The save itself doesn't delete anyone — but a scheduled or manual cleanup will
            sweep them all up. You can change the value back to a higher number before that
            happens.
          </>
        }
        confirmLabel="Yes, save"
        cancelLabel="Cancel"
        tone="danger"
      />
    </div>
  );
}
